/**
 * Bundled last-resort translations for the CRITICAL path.
 *
 * Every namespace is normally HTTP-loaded from the backend
 * (`/api/i18n/bundle/:locale/:audience/:hash`) — the backend catalog is the single
 * source of truth and is edited server-side. That makes the backend a hard
 * dependency of the very screen a user reaches when the backend is flaky:
 * `/login`. A failed namespace fetch used to leave the login form rendering
 * raw keys (`fields.email_address`) or nothing at all.
 *
 * This module bundles the SMALLEST set that keeps the app operable without a
 * backend:
 *   - `auth`   → the login surface (LoginPage + LoginForm) copy;
 *   - `common` → the two shell strings shown while the app is still loading.
 * Nothing else is bundled: everything else degrades through
 * `humanizeTranslationKey` (see ../missing-key-humanizer.ts).
 *
 * The values are copied verbatim from the backend catalog and guarded against
 * drift by `__tests__/criticalFallbackResources.test.ts`.
 */

import type { i18n as I18nInstance } from 'i18next'

/** Languages the bundled fallback covers (the platform's two active locales). */
export const CRITICAL_FALLBACK_LANGUAGES = ['en', 'es'] as const
type CriticalFallbackLanguage = (typeof CRITICAL_FALLBACK_LANGUAGES)[number]

/** Namespaces the bundled fallback covers. Keep this list minimal. */
export const CRITICAL_FALLBACK_NAMESPACES = ['auth', 'common'] as const
type CriticalFallbackNamespace = (typeof CRITICAL_FALLBACK_NAMESPACES)[number]

/** A nested i18next resource bundle (`{ page: { loginTitle: '…' } }`). */
interface TranslationBundle {
  readonly [key: string]: string | TranslationBundle
}

export type CriticalFallbackResources = Readonly<
  Record<CriticalFallbackLanguage, Readonly<Record<CriticalFallbackNamespace, TranslationBundle>>>
>

export const CRITICAL_FALLBACK_RESOURCES: CriticalFallbackResources = {
  en: {
    auth: {
      page: {
        tagline: 'Crypto Accounting Platform',
        loginTitle: 'Sign In',
        loginSubtitle: 'Enter your credentials to continue',
        firstTimeHere: 'First time here?',
        copyright: '© {{year}} Block&Taxes. All rights reserved.',
      },
      fields: {
        email_address: 'Email Address',
        password: 'Password',
        remember_me: 'Remember Me',
      },
      placeholders: {
        enterEmail: 'Enter your email',
        enterPassword: 'Enter your password',
      },
      actions: {
        sign_in: 'Sign In',
        sign_up: 'Sign Up',
        create_account: 'Create Account',
        forgot_password: 'Forgot Password?',
        showPassword: 'Show password',
        hidePassword: 'Hide password',
        cancel: 'Cancel',
      },
      modal: {
        noAccount: "Don't have an account?",
        signingIn: 'Signing in...',
      },
    },
    common: {
      common: { loading: 'Loading...' },
      i18nWrapper: { loadingTranslations: 'Loading translations...' },
      i18nDegraded: {
        message: 'Some texts could not be loaded. Parts of the screen may look unfinished.',
        retry: 'Retry',
        retrying: 'Retrying…',
        loading: 'Loading texts…',
        stillFailing: 'Still unable to load. Check your connection or try again in a few minutes.',
      },
    },
  },
  es: {
    auth: {
      page: {
        tagline: 'Plataforma de Contabilidad Crypto',
        loginTitle: 'Iniciar Sesión',
        loginSubtitle: 'Ingresa tus credenciales para continuar',
        firstTimeHere: '¿Primera vez aquí?',
        copyright: '© {{year}} Block&Taxes. Todos los derechos reservados.',
      },
      fields: {
        email_address: 'Dirección de Email',
        password: 'Contraseña',
        remember_me: 'Recordarme',
      },
      placeholders: {
        enterEmail: 'Ingresa tu email',
        enterPassword: 'Ingresa tu contraseña',
      },
      actions: {
        sign_in: 'Iniciar Sesión',
        sign_up: 'Registrarse',
        create_account: 'Crear Cuenta',
        forgot_password: '¿Olvidaste tu Contraseña?',
        showPassword: 'Mostrar contraseña',
        hidePassword: 'Ocultar contraseña',
        cancel: 'Cancelar',
      },
      modal: {
        noAccount: '¿No tienes una cuenta?',
        signingIn: 'Iniciando sesión...',
      },
    },
    common: {
      common: { loading: 'Cargando...' },
      i18nWrapper: { loadingTranslations: 'Cargando traducciones...' },
      i18nDegraded: {
        message:
          'No se han podido cargar todos los textos. Algunas pantallas pueden verse en inglés.',
        retry: 'Reintentar',
        retrying: 'Reintentando…',
        loading: 'Cargando los textos…',
        stillFailing:
          'Sigue sin poder cargarse. Comprueba tu conexión o vuelve a intentarlo en unos minutos.',
      },
    },
  },
}

/** A language the seeding may write to, and the bundled copy it is written from. */
interface SeedableLanguage {
  readonly target: string
  readonly source: CriticalFallbackLanguage
}

/**
 * Languages the seeding may touch: ONLY the ones i18next is currently
 * resolving through (`instance.languages`). The app has no fallback language
 * (the server-compiled bundle already carries English under each language's
 * own strings), so that chain is just the active language.
 *
 * A language with no bundled copy of its own (`de`, any language an admin
 * activates later) is seeded with the English copy under ITS code: a backend
 * that is down must not leave its readers with humanised keys.
 *
 * ⚠️ Seeding a language the app has NOT switched to is not merely wasteful, it
 * is actively harmful: i18next's BackendConnector treats a namespace that is
 * already in the store as loaded and never requests it —
 * `if (!options.reload && this.store.hasResourceBundle(lng, ns)) this.state[name] = 2`
 * (i18next/BackendConnector.queueLoad). Pre-seeding `es|common` while booting
 * in English therefore PERMANENTLY suppressed the real Spanish `common` fetch:
 * after login switched the app to Spanish, every key of that namespace fell
 * back to English — the navbar (all its labels resolve through
 * `fallbackNS: 'common'`) stayed English until a full page reload, while the
 * page body, whose namespaces are not bundled, was already Spanish.
 */
const resolveSeedableLanguages = (instance: I18nInstance): SeedableLanguage[] => {
  const activeChain: readonly string[] =
    instance.languages && instance.languages.length > 0
      ? instance.languages
      : instance.language
        ? [instance.language]
        : []

  return activeChain.map((target) => ({
    target,
    source: CRITICAL_FALLBACK_LANGUAGES.find((language) => language === target) ?? 'en',
  }))
}

/**
 * Seed the bundled copy for every critical namespace the backend has NOT
 * delivered, and ONLY those.
 *
 * Three invariants make this safe next to the backend-as-source-of-truth rule:
 *   1. only the ACTIVE resolution chain is seeded, so a language the user has
 *      not switched to yet still loads from the backend (see above);
 *   2. `hasResourceBundle` gates the write, so a namespace that loaded from
 *      the backend is never shadowed;
 *   3. i18next's connector merges a later successful load ON TOP of the store
 *      (`{ ...seeded, ...fetched }`), so a retry that finally succeeds wins —
 *      `reloadResources` passes `reload: true`, which bypasses invariant 1's
 *      short-circuit, so the bounded retries in ./failure-resilience still heal
 *      a seeded namespace. The order of the retries: a transient failure (network,
 *      timeout, 5xx, 429, or a bundle 404 that persists) is first retried by
 *      i18next itself, 500 ms and 1 s later; only when those run out does
 *      `failedLoading` fire, this seeding happens and the retries of
 *      ./failure-resilience start (3 s, 10 s and 30 s later).
 *
 * Returns the `lng:ns` pairs actually seeded (for logging / boot reporting).
 */
export const applyCriticalFallbackForMissingNamespaces = (instance: I18nInstance): string[] => {
  // The resource store only exists once `init()` has run its synchronous
  // phase; seeding earlier would throw.
  if (!instance.store) return []

  const seededNamespaces: string[] = []
  for (const { target, source } of resolveSeedableLanguages(instance)) {
    for (const namespace of CRITICAL_FALLBACK_NAMESPACES) {
      if (instance.hasResourceBundle(target, namespace)) continue
      instance.addResourceBundle(
        target,
        namespace,
        CRITICAL_FALLBACK_RESOURCES[source][namespace],
        true, // deep
        false, // never overwrite existing keys
      )
      seededNamespaces.push(`${target}:${namespace}`)
    }
  }
  return seededNamespaces
}
